import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Layer, useFeedbackEvent } from "@pinatagrams/fiesta";
import { Header, Main } from "@pinatagrams/fiesta/components/layout";
import { Title } from "@pinatagrams/fiesta/components/text";
import Banner from "@pinatagrams/fiesta/components/banner";
import { useEffect } from "react";

import { DashboardNavigation } from "#/components/navigation";
import { useAuthStore } from "#/stores/auth";
import { useHeaderStore } from "#/stores/header";

/**
 * Dashboard layout for authenticated users.
 * The shell (<nav> + <header>) remains constant across navigation.
 * Only the content within <Outlet /> updates when routes change.
 */
export function DashboardLayout() {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const isLoading = useAuthStore((state) => state.isLoading);
	const location = useLocation();

	// Read header state from global store
	const { title, subtitle, actions } = useHeaderStore();

	// Get banners for header - register banner for dashboard header
	const { getBannersForOrigin, registerBanner } = useFeedbackEvent();
	const banners = getBannersForOrigin("header");
	const bannerOutcome = banners.length > 0 ? banners[0] : null;

	// Register banner origin for dashboard header
	useEffect(() => {
		const unregister = registerBanner({ originId: "header" });
		return unregister;
	}, [registerBanner]);

	// Wait for auth check to complete before redirecting
	if (isLoading) {
		return (
			<Layer.Wrapper layer="main">
				<Main skeleton={true}>
					<Header>
						<Header.Title>
							<Title hierarchy="title">Loading...</Title>
						</Header.Title>
					</Header>
				</Main>
			</Layer.Wrapper>
		);
	}

	// Redirect to login if not authenticated
	if (!isAuthenticated) {
		return <Navigate to="/login" state={{ from: location.pathname }} replace />;
	}

	return (
		<>
			<Layer.Wrapper layer="nav">
				<DashboardNavigation />
			</Layer.Wrapper>
			<Layer.Wrapper layer="main">
				<Main>
					{/* Persistent header - updates via Zustand store, never unmounts */}
					<Header>
						<Header.Title>
							<Title hierarchy="title">{title}</Title>
							{subtitle && <Title hierarchy="subtitle">{subtitle}</Title>}
						</Header.Title>
						{bannerOutcome && <Banner value={bannerOutcome} />}
						{actions && <Header.Actions>{actions}</Header.Actions>}
					</Header>

					{/* Only this changes on navigation */}
					<Outlet />
				</Main>
			</Layer.Wrapper>
		</>
	);
}
